import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Button, Modal, ModalBody, ModalFooter, ModalHeader } from "reactstrap";
import { toast } from "react-toastify";

import { invokeCandidateTestActionForCallDetails } from "model/CallModel";

const buildScreeningMailPreviewDocument = (htmlBody = "") => {
    const html = String(htmlBody || "").trim();
    if (!html) return "";

    if (/<html[\s>]/i.test(html)) {
        return html;
    }

    return `<!DOCTYPE html>
<html>
<head>
<meta charset="utf-8" />
<meta name="viewport" content="width=device-width, initial-scale=1" />
</head>
<body style="margin:0;padding:20px 24px;background:#ffffff;font-family:Arial,Helvetica,sans-serif;color:#1e293b;line-height:1.5;">
${html}
</body>
</html>`;
};

const isSuccess = (response) =>
    Boolean(
        response?.success ||
            response?.isSuccess ||
            response?.status === "success" ||
            response?.status === true
    );

function CallDetailsScreeningMailPreviewModal({
    isOpen,
    onRequestClose,
    subject,
    htmlBody,
    sendAction,
    requestBody,
    callDetailsRouteParameterId,
    onSendSuccess,
}) {
    const [view, setView] = useState("preview");
    const [sendInProgress, setSendInProgress] = useState(false);
    const previewFrameRef = useRef(null);

    const previewDocument = useMemo(
        () => buildScreeningMailPreviewDocument(htmlBody),
        [htmlBody]
    );

    const resizePreviewFrame = useCallback(() => {
        const iframe = previewFrameRef.current;
        if (!iframe) return;

        try {
            const doc = iframe.contentDocument || iframe.contentWindow?.document;
            const contentHeight = Math.max(
                doc?.documentElement?.scrollHeight || 0,
                doc?.body?.scrollHeight || 0
            );

            if (contentHeight > 0) {
                const maxHeight = Math.min(window.innerHeight * 0.65, 640);
                iframe.style.height = `${Math.min(
                    Math.max(contentHeight + 12, 180),
                    maxHeight
                )}px`;
            }
        } catch {
            iframe.style.height = "240px";
        }
    }, []);

    const confirmMessage =
        sendAction?.confirmMessage ||
        "Are you sure you want to send screening mail?";

    useEffect(() => {
        if (!isOpen) {
            setView("preview");
            setSendInProgress(false);
            return;
        }

        if (view !== "preview") return;

        const frame = previewFrameRef.current;
        if (!frame) return;

        frame.style.height = "240px";
        const resizeTimer = window.setTimeout(resizePreviewFrame, 0);

        return () => window.clearTimeout(resizeTimer);
    }, [isOpen, previewDocument, resizePreviewFrame, view]);

    const handleClose = () => {
        if (sendInProgress) return;
        onRequestClose?.();
    };

    const handleSendClick = () => {
        if (!sendAction) {
            handleClose();
            return;
        }
        setView("confirm");
    };

    const handleConfirmSend = async () => {
        if (!sendAction || sendInProgress) return;

        setSendInProgress(true);
        try {
            const sendResponse = await invokeCandidateTestActionForCallDetails({
                candidateId: callDetailsRouteParameterId,
                routeKey: sendAction?.routeKey,
                routeArgument: sendAction?.routeArgument,
                requestBody,
                httpMethod: sendAction?.httpMethod,
            });

            if (isSuccess(sendResponse)) {
                toast.success(sendResponse?.message || "Sent successfully");
                await Promise.resolve(onSendSuccess?.());
                onRequestClose?.();
            } else {
                toast.error(sendResponse?.message || "Something went wrong");
            }
        } finally {
            setSendInProgress(false);
        }
    };

    if (!isOpen) {
        return null;
    }

    return (
        <Modal
            isOpen={isOpen}
            toggle={handleClose}
            centered
            size="xl"
            className="cd-screening-mail-modal modal-full-height"
        >
            <ModalHeader toggle={handleClose} className="cd-screening-mail-modal__header">
                {view === "confirm"
                    ? sendAction?.label || "Screening mail"
                    : "Screening mail"}
            </ModalHeader>

            <ModalBody className="cd-screening-mail-modal__body p-0">
                {view === "preview" ? (
                    <div className="cd-screening-mail-preview">
                        <div className="cd-screening-mail-preview__subject">
                            <span className="cd-screening-mail-preview__subject-label">
                                Subject
                            </span>
                            <span className="cd-screening-mail-preview__subject-value">
                                {subject || "Screening mail"}
                            </span>
                        </div>
                        <div className="cd-screening-mail-preview__canvas-wrap">
                            <div className="cd-screening-mail-preview__canvas">
                                <iframe
                                    ref={previewFrameRef}
                                    className="cd-screening-mail-preview__frame"
                                    title="Screening mail preview"
                                    sandbox="allow-same-origin"
                                    srcDoc={previewDocument}
                                    onLoad={resizePreviewFrame}
                                />
                            </div>
                        </div>
                    </div>
                ) : (
                    <div className="cd-screening-mail-confirm">
                        <p className="mb-0">{confirmMessage}</p>
                    </div>
                )}
            </ModalBody>

            <ModalFooter className="cd-screening-mail-modal__footer">
                {view === "preview" ? (
                    <>
                        <Button
                            type="button"
                            color="secondary"
                            outline
                            onClick={handleClose}
                        >
                            Cancel
                        </Button>
                        {sendAction ? (
                            <Button
                                type="button"
                                color="primary"
                                onClick={handleSendClick}
                            >
                                {sendAction?.label || "Screening Mail"}
                            </Button>
                        ) : (
                            <Button
                                type="button"
                                color="primary"
                                onClick={handleClose}
                            >
                                Close
                            </Button>
                        )}
                    </>
                ) : (
                    <>
                        <Button
                            type="button"
                            color="secondary"
                            outline
                            disabled={sendInProgress}
                            onClick={() => setView("preview")}
                        >
                            Back
                        </Button>
                        <Button
                            type="button"
                            color="primary"
                            disabled={sendInProgress}
                            onClick={handleConfirmSend}
                        >
                            {sendInProgress ? (
                                <>
                                    <i
                                        className="fas fa-spinner fa-spin me-1"
                                        aria-hidden="true"
                                    />
                                    Sending...
                                </>
                            ) : (
                                "Yes"
                            )}
                        </Button>
                    </>
                )}
            </ModalFooter>
        </Modal>
    );
}

export default CallDetailsScreeningMailPreviewModal;
